import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useEffect, useState, VFC } from 'react';
import { useHash } from 'react-use';

import { isCreator } from '@/apis/aniftyBackend/users/interface';
import MyAuctionList from '@/containers/AuctionActivity/MyAuctionList';
import MyBidsList from '@/containers/AuctionActivity/MyBidsList';
import useCurrentUser from '@/hooks/useCurrentUser';

import styles from './AuctionActivity.module.scss';

const Tab = {
  MyAuctions: 'myAuctions',
  MyBids: 'myBids',
} as const;

type Tab = typeof Tab[keyof typeof Tab];

const AuctionActivity: VFC = () => {
  const { t } = useTranslation();

  const [tabHash, setTabHash] = useHash();

  const { user } = useCurrentUser();

  const tabLabels: {
    [tab in Tab]: string;
  } = {
    [Tab.MyAuctions]: t('activityPage.tabs.auctions'),
    [Tab.MyBids]: t('activityPage.tabs.bids'),
  };

  const availableTabs: Tab[] = isCreator(user)
    ? [Tab.MyAuctions, Tab.MyBids]
    : [Tab.MyBids];

  const [tab, setTab] = useState<Tab>(availableTabs[0]);

  // Sync tab with hash
  useEffect(() => {
    if ((availableTabs as string[]).includes(tabHash)) {
      setTab(tabHash as Tab);
    }
  }, [tabHash]);

  // Sync hash with tab
  useEffect(() => {
    setTabHash(tab);
  }, [tab]);

  return (
    <div className="container">
      <h1 className={styles.heading}>{t('activityPage.heading')}</h1>
      <p className={styles.pageDescription}>
        {isCreator(user)
          ? t('activityPage.descriptionForCreator')
          : t('activityPage.descriptionForCollector')}
      </p>

      <nav className={styles.activityNavLink}>
        <ul>
          {availableTabs.map((_tab) => (
            <li key={_tab}>
              <button
                className={classNames({
                  [styles.activeLink]: tab === _tab,
                })}
                onClick={() => setTab(_tab)}
              >
                {tabLabels[_tab]}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {tab === Tab.MyAuctions ? <MyAuctionList /> : null}

      {tab === Tab.MyBids ? <MyBidsList /> : null}
    </div>
  );
};

export default AuctionActivity;
